/**
 * Service-level status for registered pipeline tools. Not the old Toolbench catalog.
 */
import { pipelineToolStateChipClass, pipelineToolStateLabel } from "../../../packages/core/src/pipeline-tool-status.js";
import { escapeHtml } from "./dashboard-html.js";
import { pipelineOperatorNavHtml } from "./dashboard-pipeline-nav.js";
import { authenticatedFetch } from "./dashboard-auth.js";

export type PipelineToolStatusRow = {
  toolId: string;
  title: string;
  state: string;
  failureClass: string | null;
  message: string | null;
  attempted: number | null;
  houseMisses: number;
};

export async function renderPipelineTools(mount: HTMLElement): Promise<void> {
  const payload = await loadPipelineTools();
  mount.innerHTML = pipelineToolsHtml(payload);
}

export function pipelineToolsHtml(payload: {
  generatedAt: string | null;
  persistError?: string | null;
  tools: PipelineToolStatusRow[];
} | null): string {
  const rows = payload?.tools ?? [];
  const generated = payload?.generatedAt ? new Date(payload.generatedAt).toLocaleString() : "no conveyor cycle stored yet";
  const persistError = payload?.persistError?.trim() || "";
  return `
    <div class="pipeline-shell" data-pipeline-tools>
      <header class="pipeline-top">
        <div class="wordmark">Pipeline<small>seven trees → Close</small></div>
        ${pipelineOperatorNavHtml("tools")}
      </header>
      <div class="pipeline-toolbar">
        <div>
          <h1>Tools</h1>
          <p>Service status for registered pipeline tools. A down API, a dead key, a queued lookup, or a missing payment card belongs here — not on a house. House misses are proven empty results after a successful lookup.</p>
        </div>
      </div>
      <main class="pipeline-tools-page">
        <p class="subtle">Last conveyor cycle ${escapeHtml(generated)}</p>
        ${persistError ? `<p class="subtle" data-persist-error>Snapshot persist failed: ${escapeHtml(persistError)}</p>` : ""}
        <div class="pipeline-tools-table-wrap">
        <table class="pipeline-tools-table">
          <thead>
            <tr><th>Tool</th><th>Status</th><th>Last cycle</th><th>Detail</th><th>House misses</th></tr>
          </thead>
          <tbody>
            ${rows.length ? rows.map((row) => toolRow(row)).join("") : `<tr><td colspan="5">Tool status unavailable.</td></tr>`}
          </tbody>
        </table>
        </div>
      </main>
    </div>
  `;
}

function toolRow(row: PipelineToolStatusRow): string {
  const detail = row.message || row.failureClass || "—";
  const attempted = row.attempted == null ? "—" : String(row.attempted);
  return `
    <tr data-tool-id="${escapeHtml(row.toolId)}" data-tool-state="${escapeHtml(row.state)}">
      <td>${escapeHtml(row.title)}</td>
      <td><span class="chip ${pipelineToolStateChipClass(row.state)}">${escapeHtml(pipelineToolStateLabel(row.state, row.failureClass))}</span></td>
      <td>${escapeHtml(attempted)}</td>
      <td>${escapeHtml(detail)}</td>
      <td>${row.houseMisses ? String(row.houseMisses) : "—"}</td>
    </tr>
  `;
}

async function loadPipelineTools(): Promise<{
  generatedAt: string | null;
  persistError?: string | null;
  tools: PipelineToolStatusRow[];
} | null> {
  try {
    const response = await authenticatedFetch("/dashboard/pipeline-tools");
    if (!response.ok) return null;
    return (await response.json()) as {
      generatedAt: string | null;
      persistError?: string | null;
      tools: PipelineToolStatusRow[];
    };
  } catch {
    return null;
  }
}
